<script setup>
import { onMounted, watch } from "vue";
import {
  useThemeStore,
  useAuthStore,
  useDistributionStore,
  useParticipantTimepointStore,
  useBootstrapStore,
  useLocaleStore,
  useNavStore,
  useProgressStore,
  useMaintenanceStore,
  usePausedTimepointStore,
  useReviewStore,
} from "@/stores";
import { storeToRefs } from "pinia";
import { RouterView } from "vue-router";
import BrowserBackHandler from "@/hoc/BrowserBackHandler.vue";
import DevTools from "@/views/DevToolsView.vue";
import TopNav from "@/components/layout/TopNav.vue";
import Footer from "@/components/layout/Footer.vue";
import ProgressBar from "@/components/widgets/ProgressBar.vue";
import MaintenanceNotice from "@/components/system/MaintenanceNotice.vue";
import PausedTimepointNotice from "@/components/system/PausedTimepointNotice.vue";
import ReviewDrawer from "@/components/review/ReviewDrawer.vue";

const isLocal = import.meta.env.MODE === "development";

const distributionStore = useDistributionStore();
const participantTimepointStore = useParticipantTimepointStore();
const bootstrapStore = useBootstrapStore();
const localeStore = useLocaleStore();
const authStore = useAuthStore();
const navStore = useNavStore();
const progressStore = useProgressStore();
const maintenanceStore = useMaintenanceStore();
const pausedTimepointStore = usePausedTimepointStore();
const reviewStore = useReviewStore();
const { current: currentNavStorePage } = storeToRefs(navStore);
const { isActive: isMaintenanceActive } = storeToRefs(maintenanceStore);
const { isActive: isTimepointPaused } = storeToRefs(pausedTimepointStore);

const progressablePages = [
  "welcome",
  "criteria",
  "consent",
  "contact_form",
  "questionnaire",
  "measure",
  "conclusion",
];

const contextForStep = (step) => {
  const contexts = {
    welcome: { type: "welcome" },
    criteria: { type: "criteria" },
    contact_form: { type: "contact_form" },
  };

  if (contexts[step?.name]) return contexts[step.name];
  if (step?.name === "questionnaire") {
    return { type: "questionnaire", id: step.params?.id ?? null };
  }
  if (step?.name === "measure") {
    return {
      type: "measure",
      id: step.params?.id ?? null,
      itemId: step.params?.item_id ?? null,
    };
  }
  if (step?.name === "conclusion") {
    return {
      type:
        step.params?.scenario === "criteria_no_match"
          ? "criteria_conclusion"
          : "conclusion",
    };
  }
  if (step?.name === "consent") return { type: "consent", id: null };

  return null;
};

watch(
  currentNavStorePage,
  (step) => reviewStore.setContext(contextForStep(step)),
  { immediate: true, deep: true },
);

onMounted(async () => {
  useThemeStore().initializeTheme();

  if (authStore.isAuthenticated) {
    await bootstrapStore.fetchBootstrap(
      distributionStore.distribution_id,
      participantTimepointStore.participant_timepoint_id,
    );
    // Update available languages for the UI
    localeStore.setAvailableLocales(bootstrapStore.languages);

    if (bootstrapStore.participant_language_used) {
      localeStore.setLocale(bootstrapStore.participant_language_used);
    } else if (!localeStore.locale && bootstrapStore.default_language) {
      localeStore.setLocale(bootstrapStore.default_language);
    }
  }
});
</script>

<!--
    This template requires:

    ```
    <html class="h-full">
      <body class="h-full">
        <div id="app" class="min-h-full"></div>
    ```
  -->
<template>
  <BrowserBackHandler>
    <div class="flex flex-col min-h-screen">
      <TopNav />
      <ProgressBar
        v-if="
          !isMaintenanceActive &&
          !isTimepointPaused &&
          progressablePages.includes(currentNavStorePage?.name) &&
          progressStore.visible
        "
        :percentage="progressStore.progress_percentage"
      />
      <main
        class="w-full mx-auto py-10 px-4 md:px-8 print:px-0 print:py-0"
        :class="
          isMaintenanceActive || isTimepointPaused
            ? 'flex flex-1 items-center justify-center max-w-5xl'
            : 'max-w-7xl'
        "
      >
        <MaintenanceNotice v-if="isMaintenanceActive" />
        <PausedTimepointNotice v-else-if="isTimepointPaused" />
        <RouterView v-else v-slot="{ Component }">
          <template v-if="Component">
            <component :is="Component"></component>
          </template>
        </RouterView>
      </main>
      <footer class="sticky top-[100vh] print:hidden">
        <Footer />
      </footer>
    </div>
  </BrowserBackHandler>
  <DevTools v-if="isLocal || distributionStore.distribution_is_test" />
  <ReviewDrawer v-if="authStore.isAuthenticated" />
</template>
